// Basic building blocks. Colour always comes with a label (and usually an icon): never colour-only meaning (WCAG 1.4.1).
import type { ButtonHTMLAttributes, ReactNode } from "react";

type BtnKind = "primary" | "secondary" | "ghost" | "danger";
const BTN: Record<BtnKind, string> = {
  primary: "bg-brand text-brand-ink hover:opacity-90 border border-brand",
  secondary: "border border-line bg-raised hover:bg-sunken",
  ghost: "hover:bg-sunken",
  danger: "border border-blocked-line bg-blocked-bg text-blocked hover:opacity-90",
};

export function Button({ kind = "secondary", size = "md", className = "", children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { kind?: BtnKind; size?: "sm" | "md" }) {
  return (
    <button type="button" {...rest}
      className={`inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium disabled:cursor-not-allowed disabled:opacity-50 ${size === "sm" ? "min-h-7 px-2 text-xs" : "min-h-8 px-3 text-sm"} ${BTN[kind]} ${className}`}>
      {children}
    </button>
  );
}

export function Card({ title, action, children, className = "", bodyClass = "p-4" }: { title?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; bodyClass?: string }) {
  return (
    <section className={`rounded-lg border border-line bg-raised ${className}`}>
      {(title || action) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5">
          <h2 className="text-sm font-semibold">{title}</h2>
          {action}
        </header>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

export const Kbd = ({ children }: { children: ReactNode }) => (
  <kbd className="rounded border border-line bg-sunken px-1 font-mono text-[11px] text-muted">{children}</kbd>
);

/** A progress meter for a figure the engine counted (never computed here): `value` of `max`. */
export function Meter({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <span className="inline-flex items-center gap-2" title={`${label}: ${value} of ${max}`}>
      <span role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} className="h-1.5 w-16 overflow-hidden rounded-full bg-sunken ring-1 ring-line">
        <span className="block h-full bg-done" style={{ width: `${pct}%` }} />
      </span>
      <span className="text-xs tabular-nums text-muted">{value}/{max}</span>
    </span>
  );
}

export function PageHeader({ title, subtitle, crumbs, actions }: { title: ReactNode; subtitle?: ReactNode; crumbs?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        {crumbs && <div className="mb-0.5 text-xs text-muted">{crumbs}</div>}
        <h1 className="truncate text-xl font-semibold">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

/** Empty state: always says what creates the first item — never a blank area. */
export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-line px-4 py-8 text-center">
      <p className="text-sm font-medium">{title}</p>
      {children && <p className="mt-1 text-sm text-muted">{children}</p>}
    </div>
  );
}

/** Error state: names what failed and what to do next. */
export function ErrorState({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-blocked-line bg-blocked-bg px-3 py-2 text-sm text-blocked">
      <span><span aria-hidden="true">! </span>{error}</span>
      {onRetry && <Button size="sm" onClick={onRetry}>Try again</Button>}
    </div>
  );
}

/** Skeleton rows while a list loads (not a spinner). */
export function LoadingRows({ rows = 4, label = "Loading" }: { rows?: number; label?: string }) {
  return (
    <div role="status" aria-label={label} className="space-y-2 py-1">
      {Array.from({ length: rows }, (_, i) => <div key={i} className="skeleton h-5" style={{ width: `${90 - i * 9}%` }} />)}
      <span className="sr-only">{label}…</span>
    </div>
  );
}

/** Loading / error / content in one place, so no screen is ever blank. */
export function LoadState<T>({ state, children, rows }: { state: { data?: T; error?: string; loading: boolean; reload?: () => void }; children: (data: T) => ReactNode; rows?: number }) {
  if (state.error) return <ErrorState error={state.error} onRetry={state.reload} />;
  if (state.data === undefined) return <LoadingRows rows={rows} />;
  return <>{children(state.data)}</>;
}

/** Placeholder for screens the next workstream builds; it still shows what is already published. */
export function ComingIn({ stream, what, children }: { stream: string; what: string; children?: ReactNode }) {
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-dashed border-progress-line bg-progress-bg px-4 py-3 text-sm text-progress">
        <span className="font-semibold">Coming in {stream}:</span> {what}
      </div>
      {children}
    </div>
  );
}

/** Label for agent-authored content: always violet with an agent chip (DESIGN §4). */
export function AgentChip({ name }: { name?: string | null }) {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-agent-line bg-agent-bg px-1.5 py-0.5 text-[11px] font-medium text-agent">
      <span aria-hidden="true">✦</span>{name ? `Agent · ${name}` : "Agent"}
    </span>
  );
}

export function Field({ label, hint, error, children, id, required }: { label: string; hint?: string; error?: string; children: ReactNode; id: string; required?: boolean }) {
  return (
    <div className="space-y-1">
      {/* The required mark is CSS content with empty alt text (index.css .req): seen, not read as part of the name (the input
          carries aria-required). */}
      <label htmlFor={id} className={`block text-sm font-medium${required ? " req" : ""}`}>{label}</label>
      {children}
      {hint && <p id={`${id}-hint`} className="text-xs text-muted">{hint}</p>}
      {error && <p id={`${id}-err`} role="alert" className="text-xs text-blocked">{error}</p>}
    </div>
  );
}

export const inputCls = "block w-full rounded-md border border-line bg-paper px-2.5 py-1.5 text-sm placeholder:text-muted";
